import { z } from 'zod'

/**
 * Append-only CRM stage transition log — one row every time a record enters a
 * stage. Shared by deals and contact lifecycle tracks so the stage fuel gauge
 * can answer "who moved this here, and when" for every funnel.
 */
export enum CrmStageHistoryScopeEnum {
  /** recordId = Deal.id; stages come from the org's DEAL CrmStage config. */
  DEAL = 'DEAL',
  /**
   * recordId = Contact.id, relationshipTypeId = the lifecycle track; stages
   * come from that track's ContactRelationshipTypeStage rows.
   */
  CONTACT_RELATIONSHIP = 'CONTACT_RELATIONSHIP',
}

export const CrmStageHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  scope: z.nativeEnum(CrmStageHistoryScopeEnum),
  recordId: z.string().uuid(),
  /** Only set for CONTACT_RELATIONSHIP rows. */
  relationshipTypeId: z.string().uuid().nullable(),
  /** Null when the record entered the funnel (creation / first assignment). */
  previousStageId: z.string().nullable(),
  newStageId: z.string(),
  previousStageTypeId: z.string().nullable(),
  newStageTypeId: z.string().nullable(),
  /** Who moved it — null for system/workflow/import writers. */
  changedById: z.string().uuid().nullable(),
  changedBy: z
    .object({
      id: z.string().uuid(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      imageUrl: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  createdAt: z.date(),
})

export type CrmStageHistoryEntry = z.infer<typeof CrmStageHistoryEntrySchema>

/** Default page size for a stage-history read (newest first). */
export const CRM_STAGE_HISTORY_DEFAULT_LIMIT = 100
/** Hard cap on a single stage-history read. */
export const CRM_STAGE_HISTORY_MAX_LIMIT = 500
